(() => {
  'use strict';
  if (window.TFCustomBuilder) { window.TFCustomBuilder.init(document); return; }

  class CustomBuilder {
    constructor(root) {
      this.root = root;
      this.data = JSON.parse(root.querySelector('[data-custom-data]').textContent);
      this.variants = this.data.variants || [];
      this.duration = '';
      this.step = 0;
      this.busy = false;
      this.quantities = new Map();
      this.cards = [...root.querySelectorAll('[data-component]')];
      this.form = root.querySelector('[data-custom-form]');
      this.variantInput = root.querySelector('[data-custom-variant]');
      this.lastSubmitAttempt = 0;
      this.next = root.querySelector('[data-custom-next]');
      this.submit = root.querySelector('[data-custom-submit]');
      this.error = root.querySelector('[data-custom-error]');
      this.money = cents => window.TPFU?.money ? window.TPFU.money(cents) : new Intl.NumberFormat(document.documentElement.lang || 'en-CA', { style: 'currency', currency: this.data.currency || 'CAD' }).format(cents / 100);
      this.cards.forEach(card => this.quantities.set(card.dataset.component, 0));
      const photo = root.querySelector('[data-custom-photo]');
      if (photo?.dataset.fallback) {
        const fallback = () => { photo.src = photo.dataset.fallback; };
        if (photo.complete && !photo.naturalWidth) fallback();
        else photo.addEventListener('error', fallback, { once: true });
      }
      root.querySelectorAll('[data-component-group]').forEach(group => { group.hidden = !group.querySelector('[data-component]'); });
      root.querySelectorAll('[data-custom-duration]').forEach(input => { input.checked = false; input.disabled = !this.variants.some(variant => variant.options[0] === input.value && variant.available); });
      this.bind();
      this.updateDeliveryStatus();
      this.render();
      root.querySelector('[data-custom-builder]').hidden = false;
      root.querySelector('[data-custom-fallback]').hidden = true;
      root.dataset.initialized = 'true';
    }

    variant(component) { return this.variants.find(variant => variant.options[0] === this.duration && variant.options[1] === component); }
    deliveries() { return parseInt(this.duration, 10) || 0; }
    hasDuration() { return this.deliveries() > 0 && this.variants.some(variant => variant.options[0] === this.duration && variant.available); }
    selected() { return [...this.quantities.entries()].filter(([, quantity]) => quantity > 0).map(([component, quantity]) => ({ component, quantity, variant: this.variant(component) })); }
    invalidSelection() { return this.selected().find(item => !item.variant || !item.variant.available || !this.validQuantity(item.quantity, item.variant)); }
    validQuantity(quantity, variant) {
      const rule = variant?.quantity_rule || {};
      const min = Number(rule.min) || 1;
      const increment = Number(rule.increment) || 1;
      return Number.isSafeInteger(quantity) && quantity >= min && (quantity - min) % increment === 0 && (!rule.max || quantity <= Number(rule.max));
    }
    message(message) { this.error.textContent = message; }
    status(message) { this.root.querySelector('[data-custom-status]').textContent = message; }

    bind() {
      this.root.addEventListener('click', event => {
        const target = event.target.closest('button');
        if (!target || this.busy) return;
        if (target.hasAttribute('data-quantity-change')) {
          if (!this.hasDuration()) { this.message('Choose how many meals you want first.'); return; }
          const card = target.closest('[data-component]');
          const component = card.dataset.component;
          const variant = this.variant(component);
          const rule = variant?.quantity_rule || {};
          const current = this.quantities.get(component) || 0;
          const change = Number(target.dataset.quantityChange);
          let quantity = current === 0 && change > 0 ? (Number(rule.min) || 1) : current + change * (Number(rule.increment) || 1);
          if (change < 0 && quantity < (Number(rule.min) || 1)) quantity = 0;
          if (rule.max) quantity = Math.min(quantity, Number(rule.max));
          this.quantities.set(component, Math.max(0, quantity));
          this.message(''); this.render();
        }
        if (target.hasAttribute('data-custom-preset')) this.applyPreset(target.dataset.customPreset);
        if (target.hasAttribute('data-step-link')) this.goTo(Number(target.dataset.stepLink));
        if (target.hasAttribute('data-custom-next')) this.goTo(this.step + 1);
        if (target.hasAttribute('data-custom-back')) this.goTo(this.step - 1);
        if (target.hasAttribute('data-tf-check-delivery')) {
          if (window.TPFU?.openDelivery) window.TPFU.openDelivery();
          else if (typeof window.showDeliveryVerification === 'function') window.showDeliveryVerification();
          else this.message('Delivery area checking is temporarily unavailable. Your delivery address will be confirmed at checkout.');
        }
      });
      this.root.addEventListener('change', event => {
        const target = event.target;
        if (target.matches('[data-custom-duration]')) {
          this.duration = target.checked && !target.disabled ? target.value : '';
          this.message(''); this.render();
          this.status(this.hasDuration() ? `${this.deliveries()} ${this.deliveries() === 1 ? 'meal' : 'meals'} selected. ${this.selected().length ? 'Your component quantities have been kept.' : 'Now choose the portions in your daily meal.'}` : 'Choose how many meals you want.');
        }
        if (target.matches('[data-component-quantity]')) {
          if (!this.hasDuration()) { this.render(); this.message('Choose how many meals you want first.'); return; }
          const component = target.closest('[data-component]').dataset.component;
          const quantity = Number(target.value);
          this.quantities.set(component, Number.isSafeInteger(quantity) && quantity >= 0 ? quantity : 0);
          this.message(''); this.render();
        }
        if (target.closest('[data-custom-app]')) this.renderReview();
      });
      this.form.addEventListener('input', event => { if (event.target.closest('[data-custom-app]')) this.renderReview(); });
      // The native PlateOS pipeline owns validation and paid companion items.
      // Our ancestor guard runs first, so an invalid custom meal cannot charge
      // extras. Its replay then reaches this one multi-item bubble handler.
      this.root.addEventListener('submit', event => {
        if (event.target !== this.form) return;
        if (!this.beforeSubmit()) { event.preventDefault(); event.stopPropagation(); }
      }, true);
      this.root.addEventListener('click', event => {
        if (!event.target.closest('[data-custom-submit]')) return;
        if (!this.beforeSubmit()) { event.preventDefault(); event.stopPropagation(); }
      }, true);
      this.form.addEventListener('submit', event => this.addToCart(event));
      this.appObserver = new MutationObserver(records => {
        if (records.some(record => record.type === 'attributes' && record.target.matches?.('.plateos-sdp-error,.pdo-error,.plateos-slot-error'))) this.revealAppError();
      });
      this.appObserver.observe(this.form, { subtree: true, attributes: true, attributeFilter: ['style'] });
      window.PlateOSCartForm?.refresh?.();
      this.deliveryListener = () => { if (this.root.isConnected) this.updateDeliveryStatus(); };
      window.addEventListener('focus', this.deliveryListener);
      document.addEventListener('tf:delivery-updated', this.deliveryListener);
    }

    applyPreset(key) {
      if (!this.hasDuration()) { this.message('Choose how many meals you want first.'); return; }
      const presets = {
        '4-rice': { Roti: 4, '12oz Dal': 1, '12oz Sabzi': 1, '16oz Rice': 1 },
        '8-rice': { Roti: 8, '12oz Dal': 1, '12oz Sabzi': 1, '16oz Rice': 1 },
        '6-no-rice': { Roti: 6, '12oz Dal': 1, '12oz Sabzi': 1 }
      };
      const preset = presets[key];
      if (!preset || Object.entries(preset).some(([name, quantity]) => !this.quantities.has(name) || !this.variant(name)?.available || !this.validQuantity(quantity, this.variant(name)))) {
        this.message('That combination is unavailable for this duration. Build your own combination below.'); return;
      }
      this.quantities.forEach((_, name) => this.quantities.set(name, preset[name] || 0));
      this.message(''); this.render(); this.status('Combination added. You can adjust each quantity below.');
    }

    updateDeliveryStatus() {
      try {
        const verified = sessionStorage.getItem('delivery_verified') === 'true';
        const address = sessionStorage.getItem('delivery_address');
        this.root.querySelector('[data-delivery-status]').textContent = verified && address ? `Delivery area confirmed: ${address}` : 'Check that your address is within our delivery area.';
      } catch (_) { /* The address checker remains available when browser storage is restricted. */ }
    }

    render() {
      const hasDuration = this.hasDuration();
      const selected = this.selected();
      const invalid = hasDuration ? this.invalidSelection() : undefined;
      this.root.querySelector('[data-duration-state]').textContent = hasDuration ? `${this.deliveries()} ${this.deliveries() === 1 ? 'meal' : 'meals'}, delivered one per weekday. Now make each meal yours.` : 'Choose the number of meals to get started.';
      this.root.querySelector('.tf-custom__duration').toggleAttribute('data-chosen', hasDuration);
      this.cards.forEach(card => {
        const component = card.dataset.component;
        const variant = this.variant(component);
        const quantity = this.quantities.get(component) || 0;
        card.toggleAttribute('data-selected', quantity > 0);
        card.toggleAttribute('data-unavailable', hasDuration && !variant?.available);
        card.querySelector('[data-component-price]').textContent = variant ? this.money(variant.price / this.deliveries()) : '—';
        card.querySelector('[data-component-status]').textContent = !hasDuration ? '' : !variant?.available ? 'Unavailable for this duration' : quantity > 0 && !this.validQuantity(quantity, variant) ? 'Adjust quantity for this duration' : '';
        const input = card.querySelector('[data-component-quantity]');
        input.value = quantity;
        input.disabled = this.busy || !hasDuration || !variant?.available && quantity === 0;
        input.step = Number(variant?.quantity_rule?.increment) || 1;
        if (variant?.quantity_rule?.max) input.max = variant.quantity_rule.max; else input.removeAttribute('max');
        card.querySelector('[data-quantity-change="-1"]').disabled = this.busy || !hasDuration || quantity === 0;
        card.querySelector('[data-quantity-change="1"]').disabled = this.busy || !hasDuration || !variant?.available || Boolean(variant.quantity_rule?.max && quantity >= variant.quantity_rule.max);
      });
      const total = selected.reduce((sum, item) => sum + (item.variant?.price || 0) * item.quantity, 0);
      const list = this.root.querySelector('[data-summary-items]');
      list.replaceChildren(...selected.map(item => this.itemRow(`${item.quantity} × ${item.component}`, item.variant ? this.money(item.variant.price * item.quantity) : 'Unavailable')));
      list.hidden = !selected.length;
      this.root.querySelector('[data-summary-empty]').hidden = selected.length > 0;
      this.root.querySelector('[data-summary-deliveries]').textContent = hasDuration ? `${this.deliveries()} ${this.deliveries() === 1 ? 'meal · 1 weekday delivery' : 'meals · ' + this.deliveries() + ' weekday deliveries'}` : 'Choose your number of meals';
      const showTotal = hasDuration && selected.length > 0 && !invalid;
      this.root.querySelector('[data-summary-daily]').textContent = showTotal ? this.money(total / this.deliveries()) : '—';
      this.root.querySelector('[data-summary-total]').textContent = showTotal ? this.money(total) : '—';
      const priceNote = this.root.querySelector('[data-summary-price-note]');
      priceNote.hidden = showTotal;
      priceNote.textContent = !hasDuration ? 'Choose your meals and portions to see your total.' : invalid ? 'Adjust the unavailable components to see your total.' : 'Add your portions to see your total.';
      this.next.disabled = this.busy || !hasDuration || selected.length === 0 || Boolean(invalid);
      this.submit.disabled = this.busy || !hasDuration || selected.length === 0 || Boolean(invalid);
      if (invalid) this.message(`${invalid.component} is unavailable or needs a different quantity for ${this.deliveries()} deliveries. Adjust it or choose another duration.`);
      this.root.querySelectorAll('[data-custom-preset]').forEach(button => {
        const names = ['Roti', '12oz Dal', '12oz Sabzi', ...(button.dataset.customPreset.includes('no-rice') ? [] : ['16oz Rice'])];
        button.disabled = this.busy || !hasDuration || names.some(name => !this.quantities.has(name) || !this.variant(name)?.available);
      });
      const nativeVariant = hasDuration && selected.length && !invalid ? selected[0].variant : null;
      const nextId = nativeVariant ? String(nativeVariant.id) : '';
      const variantChanged = this.variantInput.value !== nextId;
      this.variantInput.value = nextId;
      this.variantInput.disabled = !nativeVariant;
      if (variantChanged) {
        this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant: nativeVariant } }));
        document.dispatchEvent(new CustomEvent('shopify:variant_change', { detail: { variant: nativeVariant } }));
      }
      window.PlateOSCartForm?.refresh?.();
      this.renderReview();
    }

    itemRow(name, price) {
      const row = document.createElement('li');
      const title = document.createElement('span'); const amount = document.createElement('span');
      title.textContent = name; amount.textContent = price;
      row.append(title, amount); return row;
    }

    properties() {
      const properties = {};
      for (const [key, value] of new FormData(this.form)) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && typeof value === 'string' && value.trim()) properties[match[1]] = value;
      }
      return properties;
    }

    renderReview() {
      this.root.querySelector('[data-review-items]').replaceChildren(...this.selected().map(item => this.itemRow(`${item.quantity} × ${item.component}`, item.variant ? `${this.money(item.variant.price * item.quantity)} / package` : 'Unavailable')));
      this.root.querySelector('[data-review-duration]').textContent = this.hasDuration() ? `${this.deliveries()} weekday ${this.deliveries() === 1 ? 'delivery' : 'deliveries'}` : 'Not selected';
      const list = this.root.querySelector('[data-custom-review-properties]');
      list.replaceChildren(...Object.entries(this.properties()).filter(([key]) => !key.startsWith('_')).map(([key, value]) => {
        const row = document.createElement('div'); const term = document.createElement('dt'); const description = document.createElement('dd');
        term.textContent = key; description.textContent = value; row.append(term, description); return row;
      }));
      const extraCount = this.form.querySelectorAll('.pao-list input[type="checkbox"]:checked').length;
      const paidDelivery = this.form.querySelector('.pdo-select')?.selectedOptions[0]?.dataset.variant || this.form.querySelector('.pdo-choice input:checked')?.dataset.variant;
      this.root.querySelector('[data-custom-extra-cost]').hidden = !extraCount && !paidDelivery;
    }

    validateMeal() {
      if (!this.hasDuration()) {
        this.message('Choose how many meals you want before continuing.');
        if (!this.root.querySelector('[data-custom-step="0"]').hidden) this.root.querySelector('[data-custom-duration]:not(:disabled)')?.focus({ preventScroll: true });
        return false;
      }
      if (!this.selected().length) { this.message('Add at least one component to build your meal.'); return false; }
      const invalid = this.invalidSelection();
      if (invalid) { this.message(`Please adjust ${invalid.component} or choose another duration before continuing.`); return false; }
      return true;
    }

    showDeliveryProblem(message, field) {
      if (this.step !== 1) this.goTo(1, true);
      this.message(message);
      if (field) {
        field.closest('details')?.setAttribute('open', '');
        field.focus?.({ preventScroll: true });
        field.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      }
      return false;
    }

    validateDelivery() {
      const selectors = {
        'start-date-picker': '[data-testid="plateos-startdate-widget"]',
        'delivery-options': '[data-testid="plateos-delivery-options-widget"]',
        'delivery-time-slot': '[data-testid="plateos-delivery-slot-widget"]'
      };
      for (const [kind, selector] of Object.entries(selectors)) {
        if ((this.data.appBlocks || []).some(type => type.includes('/' + kind + '/')) && !this.form.querySelector(selector)) return this.showDeliveryProblem('Delivery options are still loading. Please try again.', this.root.querySelector('[data-custom-delivery-apps]'));
      }
      const date = this.form.querySelector('[data-testid="plateos-startdate-widget"][data-require-date="true"]');
      const dateInput = date?.querySelector('.plateos-sdp-input');
      if (dateInput && !dateInput.value) return this.showDeliveryProblem('Choose your first delivery date to continue.', dateInput);
      const delivery = this.form.querySelector('[data-testid="plateos-delivery-options-widget"][data-required="true"]');
      if (delivery) {
        const select = delivery.querySelector('.pdo-select');
        const selected = select ? select.selectedOptions[0] : delivery.querySelector('input[type="radio"]:checked');
        if (!selected?.value) return this.showDeliveryProblem('Choose your delivery instructions to continue.', select || delivery);
        const other = delivery.querySelector('.pdo-other');
        if (selected.value === '__other__' && !other?.value.trim()) return this.showDeliveryProblem('Add your delivery note to continue.', other);
      }
      const slot = this.form.querySelector('[data-testid="plateos-delivery-slot-widget"]');
      // When PlateOS disables slots for the account it hides this widget.
      // When enabled, its native selected-state and gate remain authoritative.
      if (slot && slot.style.display !== 'none' && !slot.querySelector('.plateos-slot-choice.is-selected')) return this.showDeliveryProblem('Check your delivery area and choose an available time to continue.', slot.querySelector('.plateos-slot-input,.plateos-slot-select,.plateos-slot-choice') || slot);
      const fields = [...this.root.querySelector('[data-custom-step="1"]').querySelectorAll('input, select, textarea')];
      const invalid = fields.find(field => !field.disabled && field.willValidate && !field.checkValidity());
      if (invalid) return this.showDeliveryProblem('Complete the required delivery details.', invalid);
      return true;
    }

    beforeSubmit() {
      this.lastSubmitAttempt = Date.now();
      if (this.busy) return false;
      if (!this.validateMeal()) { this.goTo(0, true); this.validateMeal(); return false; }
      if (this.step !== 2) { this.goTo(this.step + 1); return false; }
      if (!this.validateDelivery()) return false;
      const terms = this.root.querySelector('[data-custom-terms]');
      if (terms && !terms.checked) { this.message('Please read and accept the terms before adding your package.'); terms.reportValidity(); return false; }
      requestAnimationFrame(() => this.revealAppError());
      return true;
    }

    revealAppError() {
      if (Date.now() - this.lastSubmitAttempt > 3000) return;
      const error = [...this.form.querySelectorAll('.plateos-sdp-error,.pdo-error,.plateos-slot-error')].find(node => node.style.display !== 'none' && node.textContent.trim());
      if (error) this.showDeliveryProblem(error.textContent.trim(), error);
    }

    goTo(step, skipValidation = false) {
      if (this.busy || step < 0 || step > 2) return;
      this.message('');
      if (!skipValidation && step > this.step) {
        if (!this.validateMeal()) return;
        if (step > 1) {
          if (this.step === 0) this.goTo(1, true);
          if (!this.validateDelivery()) return;
        }
      }
      this.step = step;
      this.root.querySelectorAll('[data-custom-step]').forEach(panel => { panel.hidden = Number(panel.dataset.customStep) !== step; });
      this.root.querySelectorAll('.tf-custom__steps [data-step-link]').forEach(button => {
        if (Number(button.dataset.stepLink) === step) button.setAttribute('aria-current', 'step'); else button.removeAttribute('aria-current');
      });
      this.root.querySelector('[data-custom-back]').hidden = step === 0;
      this.next.hidden = step === 2;
      this.submit.hidden = step !== 2;
      this.next.replaceChildren(document.createTextNode(step === 0 ? 'Continue to delivery →' : 'Review your package →'));
      this.renderReview();
      const heading = this.root.querySelector(`[data-custom-step="${step}"] h2`);
      heading.focus({ preventScroll: true });
      heading.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
      this.updateDeliveryStatus();
    }

    async addToCart(event) {
      if (event.defaultPrevented) return;
      event.preventDefault();
      if (this.busy) return;
      this.message('');
      window.PlateOSCartForm?.refresh?.();
      // Keep every native widget property, including private PlateOS values,
      // unchanged on each selected real Shopify component line.
      const properties = this.properties();
      const items = this.selected().map(item => ({ id: item.variant.id, quantity: item.quantity, properties: { ...properties } }));
      this.busy = true;
      this.form.setAttribute('aria-busy', 'true');
      this.submit.textContent = 'Adding your package…';
      this.render();
      try {
        const response = await fetch(this.data.cartAddUrl, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(typeof result.description === 'string' ? result.description : typeof result.message === 'string' ? result.message : 'Your package could not be added. Please try again.');
        document.dispatchEvent(new CustomEvent('tf:cart-updated', { detail: result }));
        this.status('Your package has been added. Opening your cart.');
        window.location.assign(this.data.cartUrl);
      } catch (error) {
        this.busy = false;
        this.form.removeAttribute('aria-busy');
        this.submit.textContent = 'Add package to cart →';
        this.render();
        this.message(error.message || 'We could not reach the cart. Please try again.');
      }
    }

    destroy() { this.appObserver?.disconnect(); window.removeEventListener('focus', this.deliveryListener); document.removeEventListener('tf:delivery-updated', this.deliveryListener); }
  }

  const instances = new WeakMap();
  const init = container => {
    const roots = container.matches?.('[data-tf-custom]') ? [container] : [...container.querySelectorAll('[data-tf-custom]')];
    roots.forEach(root => {
      if (instances.has(root)) return;
      try { instances.set(root, new CustomBuilder(root)); }
      catch (_) { root.querySelector('[data-custom-fallback]').hidden = false; }
    });
  };
  window.TFCustomBuilder = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true }); else init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => { event.target.querySelectorAll('[data-tf-custom]').forEach(root => instances.get(root)?.destroy()); });
})();
